import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { ShoppingBag, ChevronDown, Menu, X, Search } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { SearchDialog } from "@/components/SearchDialog";
import { useSiteSettings, isNavVisible } from "@/hooks/useSiteSettings";
import { DEFAULT_SITE_SETTINGS } from "@/lib/siteSettings";
import markAsset from "@/assets/coyote-creek-wordmark-v2.svg.asset.json";

interface SubmenuItem {
  label: string;
  to: string;
  params?: Record<string, string>;
}

interface NavItem {
  label: string;
  to?: string;
  href?: string;
  params?: Record<string, string>;
  submenu?: SubmenuItem[];
}

const navItems: NavItem[] = [
  {
    label: "Hats",
    to: "/collections/$collection",
    params: { collection: "all-hats" },
  },
  {
    label: "Tops",
    to: "/collections/$collection",
    params: { collection: "tops" },
  },
  {
    label: "Custom Hats",
    href: "https://gwestcustoms.com",
  },
  {
    label: "About Us",
    submenu: [
      { label: "Heritage", to: "/heritage" },
      { label: "News", to: "/news" },
      { label: "The Standard", to: "/the-standard" },
      { label: "Our Values", to: "/our-values" },
      { label: "Range Map", to: "/range-map" },
    ],
  },
  {
    label: "Support",
    submenu: [
      { label: "FAQs", to: "/faqs" },
      { label: "Contact Us", to: "/contact" },
      { label: "Become a Dealer", to: "/wholesale" },
      { label: "Creator Creek", to: "/creator-creek" },
    ],
  },
];

export function Header({ onCartOpen }: { onCartOpen: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const totalItems = useCartStore(s => s.items.reduce((sum, i) => sum + i.quantity, 0));
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { pathname } = useLocation();
  const { data: settings } = useSiteSettings();
  const navVisibility = settings?.nav_visibility;
  const headerBg = settings?.header_bg_color ?? DEFAULT_SITE_SETTINGS.header_bg_color;
  const headerText = settings?.header_text_color ?? DEFAULT_SITE_SETTINGS.header_text_color;
  const headerLogo = settings?.header_logo_color ?? headerText;


  const filteredNavItems = navItems
    .filter((item) => isNavVisible(navVisibility, item.label))
    .map((item) => ({
      ...item,
      submenu: item.submenu?.filter((s) => isNavVisible(navVisibility, s.label)),
    }))
    .filter((item) => !item.submenu || item.submenu.length > 0 || item.to || item.href);
  const activeTextColor = headerText;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setOpenMenu(label);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setOpenMenu(null);
    }, 150);
  };

  return (
    <header
      style={{ backgroundColor: headerBg }}
      className={`relative z-50 w-full transition-all duration-300 ${
        scrolled ? "shadow-lg" : ""
      }`}
    >
      <div className="relative flex h-[88px] w-full items-center justify-between px-4 sm:px-8">
        {/* Logo (centered) */}
        <Link to="/" aria-label="Coyote Creek Hat Co." className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center">
          <span
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 h-14 w-48 -translate-x-1/2 -translate-y-1/2 sm:h-14 sm:w-60"
            style={{
              backgroundColor: headerLogo,
              WebkitMaskImage: `url(${markAsset.url})`,
              maskImage: `url(${markAsset.url})`,
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              maskPosition: "center",
              WebkitMaskSize: "contain",
              maskSize: "contain",
            }}
          />
          <span className="block h-14 w-48 sm:h-14 sm:w-60" aria-hidden="true" />
        </Link>

        {/* Left icons - mobile/tablet only: menu + search */}
        <div className="flex items-center gap-3 xl:hidden">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ color: activeTextColor }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            className="transition-opacity hover:opacity-70"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <button onClick={() => setSearchOpen(true)} aria-label="Search products" style={{ color: activeTextColor }} className="transition-opacity hover:opacity-70">
            <Search className="h-5 w-5" />
          </button>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-6 xl:flex">
          {filteredNavItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.submenu ? handleMouseEnter(item.label) : undefined}
              onMouseLeave={() => item.submenu ? handleMouseLeave() : undefined}
            >
              {item.href ? (
                <a
                  href={item.href}
                  style={{ color: activeTextColor }}
                  className="font-heading text-lg font-black uppercase tracking-[0.15em] transition-opacity duration-150 hover:opacity-70"
                >
                  {item.label}
                </a>
              ) : item.to ? (
                <Link
                  to={item.to}
                  params={item.params}
                  style={{ color: activeTextColor }}
                  className="font-heading text-lg font-black uppercase tracking-[0.15em] transition-opacity duration-150 hover:opacity-70"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  style={{ color: activeTextColor }}
                  className="flex items-center gap-1.5 font-heading text-lg font-black uppercase tracking-[0.15em] transition-opacity duration-150 hover:opacity-70"
                >
                  {item.label}
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openMenu === item.label ? 'rotate-180' : ''}`} />
                </button>
              )}

              {/* Dropdown */}
              {item.submenu && openMenu === item.label && (
                <div
                  className="absolute left-1/2 top-full pt-3 -translate-x-1/2"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="min-w-[220px] rounded-sm bg-[#FDFAF3] py-2 shadow-xl">
                    {item.submenu.map((sub, idx) => (
                      <div key={sub.label}>
                        <Link
                          to={sub.to}
                          params={sub.params}
                          className="block px-5 py-3 font-heading text-xs font-black uppercase tracking-[0.15em] transition-colors duration-150"
                          style={{ color: '#2A1510' }}
                          onMouseOver={(e) => (e.currentTarget.style.color = '#fa5021')}
                          onMouseOut={(e) => (e.currentTarget.style.color = '#2A1510')}
                          onClick={() => setOpenMenu(null)}
                        >
                          {sub.label}
                        </Link>
                        {idx < item.submenu!.length - 1 && (
                          <div className="mx-4" style={{ borderBottom: '1px solid #D4CCBC' }} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right icons: cart (always) + search (desktop only) */}
        <div className="ml-auto flex items-center gap-3 sm:gap-5">
          <button onClick={() => setSearchOpen(true)} aria-label="Search products" style={{ color: activeTextColor }} className="hidden transition-opacity hover:opacity-70 xl:block">
            <Search className="h-5 w-5" />
          </button>
          <button onClick={onCartOpen} aria-label={`Open cart${totalItems > 0 ? ` (${totalItems} items)` : ""}`} style={{ color: activeTextColor }} className="relative transition-opacity hover:opacity-70">
            <ShoppingBag className="h-5 w-5" />
            {totalItems > 0 && (
              <span style={{ color: '#F5EFE2' }} className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#FA5021] font-ui text-[10px] font-bold">
                {totalItems}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div style={{ backgroundColor: headerBg }} className="border-t border-white/20 px-6 pb-6 xl:hidden">
          <div className="flex flex-col gap-1 pt-4">
            {filteredNavItems.map((item) => (
              <div key={item.label}>
                {item.href ? (
                  <a
                    href={item.href}
                    style={{ color: headerText }}
                    className="block py-3 font-heading text-sm font-black uppercase tracking-[0.15em]"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </a>
                ) : item.to ? (
                  <Link
                    to={item.to}
                    params={item.params}
                    style={{ color: headerText }}
                    className="block py-3 font-heading text-sm font-black uppercase tracking-[0.15em]"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      style={{ color: headerText }}
                      className="flex w-full items-center justify-between py-3 font-heading text-sm font-black uppercase tracking-[0.15em]"
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileExpanded === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileExpanded === item.label && item.submenu && (
                      <div className="ml-4 flex flex-col gap-0 border-l border-white/20 pl-4">
                        {item.submenu.map((sub, idx) => (
                          <div key={sub.label}>
                            <Link
                              to={sub.to}
                              params={sub.params}
                              style={{ color: headerText, opacity: 0.8 }}
                              className="block py-2.5 font-heading text-xs font-bold uppercase tracking-[0.12em]"
                              onClick={() => setMobileOpen(false)}
                            >
                              {sub.label}
                            </Link>
                            {idx < item.submenu!.length - 1 && (
                              <div className="border-b border-white/10" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <div className="border-b border-white/10" />
              </div>
            ))}
          </div>
        </div>
      )}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
